import React, { Fragment, useContext, useEffect, useState } from 'react'
import { Box, ModalClose } from '@mui/joy';
import { DataGridPro } from '@mui/x-data-grid-pro'
import { Cell } from '../ListElements'
import ListToolbar from './ListToolbar'
import { useDataProvider } from '../../../mtska-frontend-data-provider'
import { Text } from '../Typography'
import { Button } from '../Button'
import { useTranslation } from 'react-i18next'
import { pick } from 'dot-object'
import { BreadCrumb } from '../Drawer'
import { getCustomFilterOperators, getCustomLocaleText, moveArrayElement } from './list.functions'
import { CustomFooter } from './customFooter/CustomFooter';
import { AppContext } from 'src/mtska-frontend-app-scaffolding'
import { useDebounce } from '../../../mtska-frontend-app-scaffolding/hooks/useDebounce'
import { DrawerContext } from '../Drawer/DrawerContext'

const listId = Math.random().toString(16).slice(2);

const pageSizeOptions = [5, 10, 15, 20, 100, 250, 500];

const List = ({
    title,
    apiRef,
    isDrawer,
    uid,
    handleClose,
    dataProviderName,
    isEmbedded,
    defaultSelectedValues,
    enableSelection,
    disableMultipleRowSelection,
    rowSelectionExternalListener,
    leftButtons = [],
    rightButtons = [],
    hideTableToolbar = false,
    className = "",
    filters: filtersFromProps,
    detailStack,
    dynamicColumns = false,
    onRowSelectionModelChange = () => { },
    onFilterPageSortChange = () => { },
    items,
    total,
    loading,
    onRowDoubleClick = () => { }
}) => {

    const { t } = useTranslation()

    const { item: config } = useDataProvider('configs/config')

    const { saveSetting, settings, setSettings } = useContext(AppContext)

    const [localSettings, setLocalSettings] = useState(settings)

    const { actives = [] } = useContext(DrawerContext)
    const isNotDrawer = actives.length === 0

    useEffect(() => {
        if (Object.keys(model).length > 0 && !loading && isNotDrawer) {
            setModelField({ ...filtersFromProps })
        }
    }, [filtersFromProps])

    useEffect(() => {
        if (!localSettings) {
            setLocalSettings(settings)
        }
    }, [settings])

    const [columns, setColumns] = useState([])

    const [model, setModel] = useState({})
    const { columnOrder, filterModel, paginationModel, pinnedColumns = {}, sortModel, visibilityModel } = model

    const onChangeSettings = ({ name, settings, value }) => {
        if (settings && JSON.stringify(settings[name]) !== JSON.stringify(value)) {
            const newSettings = { ...settings }
            newSettings[name] = value
            setLocalSettings(newSettings)
            if (isNotDrawer) {
                setSettings(newSettings)
                saveSetting({ canReloadCounter: false, name, value })
            }
        }
        onFilterPageSortChange(value)
    }

    const debouncedFunction = useDebounce([{ callback: onChangeSettings }])

    const setModelField = (newModelFields = {}) => {
        const value = { ...model, ...newModelFields }
        const { items: preItems, quickFilterValues: preSearchValue } = model.filterModel || {}
        const { items: newItems, quickFilterValues: newSearchValue } = value.filterModel || {}

        const name = `${dataProviderName}.model`

        if (JSON.stringify(model.filterModel) !== JSON.stringify(value.filterModel)) {
            value.paginationModel.page = 0
        }

        if (
            JSON.stringify(preItems) !== JSON.stringify(newItems) ||
            JSON.stringify(preSearchValue) !== JSON.stringify(newSearchValue)
        ) {
            debouncedFunction({ name, settings: localSettings, value })
        } else {
            onChangeSettings({ name, settings: localSettings, value })
        }

        setModel(value)
    }

    const setColumnOrderChange = ({ oldIndex, targetIndex }) => {
        const tempColumnOrder = [...columnOrder]
        const newColumnOrder = moveArrayElement(tempColumnOrder, oldIndex - 1, targetIndex - 1)
        setModelField({ columnOrder: newColumnOrder })
    }

    const setPaginationModel = (newModel) => {
        const newPaginationModel = { ...paginationModel, ...newModel }
        setModelField({ paginationModel: newPaginationModel })
    }

    // LOAD DATA prova commit

    // Some API clients return undefined while loading
    // Following lines are here to prevent `rowCount` from being undefined during the loading
    const rowCountRef = React.useRef(total || 0);

    const rowCount = React.useMemo(() => {
        if (total !== undefined) {
            rowCountRef.current = total;
        }
        return rowCountRef.current;
    }, [total]);

    // *******************************************************************
    // MANAGE ROW SELECTION & ASSOCIATION
    // *******************************************************************

    const [rowSelectionModel, setRowSelectionModel] = useState([]);

    const hadleRowSelectionModelChange = (newRowSelectionModel) => {
        setRowSelectionModel(newRowSelectionModel);
        rowSelectionExternalListener && rowSelectionExternalListener(newRowSelectionModel);
        onRowSelectionModelChange && onRowSelectionModelChange(rowSelectionModel)
    }

    const [viewedItems, setViewedItems] = useState([]);

    // preset rowSelectionModel and viewedItems
    useEffect(() => {
        if (!enableSelection) {
            return;
        }

        if (!Array.isArray(defaultSelectedValues)) {
            setRowSelectionModel([]);
            return;
        }

        const tmp = defaultSelectedValues.reduce((a, v) => ({ ...a, [v.id]: v }), {});
        setViewedItems(tmp);

        const selectedIemsIds = defaultSelectedValues.map(el => el._id);
        setRowSelectionModel(selectedIemsIds);

    }, [defaultSelectedValues, enableSelection]);

    const localHandleSubmit = () => {
        if (!enableSelection) return
        const selectedIems = rowSelectionModel.map(id => viewedItems[id])
        handleClose(uid, selectedIems)
    }

    const handleRowDoubleClick = (row) => {
        onRowDoubleClick(row);
    }

    // add items to viewedItems when age or search change.. to have the full row object available for association
    useEffect(() => {
        if (!Array.isArray(items)) return;
        const tmp = { ...viewedItems, ...items.reduce((a, v) => ({ ...a, [v.id]: v }), {}) };
        setViewedItems(tmp);
    }, [items]);

    useEffect(() => {
        if (dynamicColumns || !localSettings || !config?.data?.modules[dataProviderName]?.tableConfig) return
        const { tableConfig = {} } = config?.data?.modules[dataProviderName]
        const { columns: columnOrder, columnVisibilityModel: visibilityModel } = tableConfig
        const defaultModel = { columnOrder, paginationModel: { page: 0, pageSize: 20 }, visibilityModel }
        if (Object.keys(model).length === 0) {
            if (localSettings[`${dataProviderName}.model`] && isNotDrawer) {
                setModelField({
                    ...defaultModel,
                    ...localSettings[`${dataProviderName}.model`]
                })
            } else if (columnOrder && visibilityModel) {
                setModelField(defaultModel)
            }
        }
    }, [dynamicColumns, localSettings])

    useEffect(() => {

        if (dynamicColumns || !settings || !config?.data?.modules[dataProviderName]?.tableConfig) return

        const { tableConfig } = config?.data?.modules[dataProviderName]

        if (Array.isArray(tableConfig?.columns)) {

            const { filterModel, visibilityModel } = model
            const { items = [] } = filterModel || {}

            const columns = tableConfig.columns.map((cc) => {

                const value = items.find(item => item.field === cc)?.value

                const { options, type } = tableConfig.columnsRendered[cc] || {}
                const filterOperators = [getCustomFilterOperators({ options, t, type, value })]
                const customFields = options || type === 'date' || type === 'datetime' ? { filterOperators } : {}
                return {
                    flex: 1,
                    field: cc,
                    headerName: t(cc.replace("_related.", "")),
                    hideable: visibilityModel === false || tableConfig.columnsRendered[cc]?.hideable === false ? false : true,
                    minWidth: type === 'date' ? 220 : 150,
                    renderCell: (params) => {
                        const columnRendered = tableConfig.columnsRendered[params?.field]
                        const drawerRoute = tableConfig.columnsRendered[params?.field]?.drawerRoute
                        const fieldDataProviderName = tableConfig.columnsRendered[params?.field]?.providerName;
                        const val = typeof params?.row === 'object' ? pick(cc, params.row) : params.value?.value;
                        return (
                            <Fragment>
                                <Cell
                                    type={columnRendered?.type}
                                    columnRendered={columnRendered}
                                    drawerRoute={drawerRoute}
                                    dataProviderName={dataProviderName}
                                    fieldDataProviderName={fieldDataProviderName}
                                    field={params?.field}
                                    value={val}
                                    row={params.row}
                                    filters={filtersFromProps}
                                    detailStack={detailStack}
                                />
                            </Fragment>
                        )
                    },
                    ...customFields
                }
            })
            setColumns(columns)
        }

    }, [dynamicColumns, settings, model])

    useEffect(() => {
        if (!dynamicColumns) return;
        if (typeof items[0] === 'undefined') return;

        const cols = Object.getOwnPropertyNames(items[0]).reduce((a, v) => {
            if (v === 'id') return [...a];
            if (v === '_id') return [...a];
            if (v === 'job_id') return [...a];
            if (v === 'checks') return [...a];

            return [...a,
            {
                flex: 1,
                field: v,
                headerName: t(v),
                // cellClassName: (params) => {
                //     const error = params.row?.errors?.[params.field];
                //     return error;
                // },
                renderCell: (params) => {
                    // const columnRendered = tableConfig.columnsRendered[params?.field]
                    // const drawerRoute = tableConfig.columnsRendered[params?.field]?.drawerRoute
                    // const fieldDataProviderName = tableConfig.columnsRendered[params?.field]?.providerName;
                    const checks = params.row?.checks?.[params.field] ?? '';
                    const bgColor = checks?.exists === false ? 'red' : (checks?.changes === true) ? 'yellow' : '';
                    const color = checks === '' ? 'lightgray' : 'darkgreen';
                    const val = typeof params?.row === 'object' ? pick(v, params.row) : params.value?.value;
                    return (
                        <Box sx={{ backgroundColor: bgColor, color: { color } }}  >
                            {/* {checks && (<Chip color='warning'>{JSON.stringify(checks)}</Chip>)} */}
                            {val}
                        </Box>
                    )
                }
            }
            ]
        }, [
            // {
            //     field: 'actions',
            //     type: 'actions',
            //     getActions: (params) => [
            //         <GridActionsCellItem label="Import" showInMenu />,
            //         <GridActionsCellItem label="Ignore" showInMenu />,
            //         <GridActionsCellItem label="Delete" showInMenu />
            //     ]
            // }, 
            // {
            //     field: "checks",
            //     type: "string",
            //     valueGetter: (value, row) => {

            //         return JSON.stringify(value);
            //       },
            // }
        ]);
        setColumns(cols);

    }, [items])

    const orderedColumns = (columnOrder || []).map((colId) =>
        columns.find((column) => column.field === colId)
    ).filter(Boolean)

    return (
        <>
            {isDrawer && (
                <>
                    <Box className="drawer-section-header" >
                        <ModalClose />
                        <Text level="title-md" icon >{title}</Text>
                        <Button label="Save" className="button-main" onClick={localHandleSubmit} />
                    </Box>
                    <BreadCrumb />
                </>
            )}
            <Box className={isEmbedded ? className + " form-element" : "drawer-section-content"}>
                <Box className={(className + " list-system listy-system-" + listId).trim()}>
                    <DataGridPro
                        apiRef={apiRef}
                        columns={orderedColumns}
                        columnVisibilityModel={visibilityModel}
                        filterModel={filterModel}
                        pinnedColumns={pinnedColumns}
                        sortModel={sortModel || []}
                        onColumnOrderChange={(params = {}) => setColumnOrderChange(params)}
                        onColumnVisibilityModelChange={(visibilityModel) => setModelField({ visibilityModel })}
                        onFilterModelChange={(filterModel) => setModelField({ filterModel })}
                        onPinnedColumnsChange={(pinnedColumns) => setModelField({ pinnedColumns })}
                        onSortModelChange={(sortModel) => setModelField({ sortModel })}
                        slots={!hideTableToolbar ? { toolbar: ListToolbar, footer: CustomFooter } : { footer: CustomFooter }}
                        slotProps={{
                            toolbar: {
                                leftButtons: leftButtons,
                                rightButtons: rightButtons,
                            },
                            footer: {
                                onPageChange: (newModel) => setPaginationModel(newModel),
                                pageSizeOptions,
                                paginationModel,
                                rowCount
                            },
                        }}
                        disableRowSelectionOnClick
                        checkboxSelection={enableSelection}
                        disableMultipleRowSelection={disableMultipleRowSelection}
                        onRowSelectionModelChange={hadleRowSelectionModelChange}
                        rowSelectionModel={rowSelectionModel}
                        getRowId={(row) => row._id}
                        autoHeight={true}
                        rows={items ?? []}
                        pagination
                        paginationMode="server"
                        rowsPerPage={10}
                        loading={loading}
                        rowCount={rowCount}
                        paginationModel={paginationModel}
                        hideFooter={!hideTableToolbar ? false : true}
                        filterMode="server"
                        sortingMode="server"
                        keepNonExistentRowsSelected
                        // onRowClick={handleRowDoubleClick}
                        onRowDoubleClick={handleRowDoubleClick}
                        className={"list-wrapper list-wrapper-" + listId}
                        headerFilters
                        localeText={getCustomLocaleText({ t })}
                    />
                </Box>
            </Box>
        </>
    )
}

export default List
